import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Activity,
  ActivityData,
  ActivityDataInput,
  NewActivity
} from "@/entities/activities/types";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import Modal from "@/components/controls/Modal";
import Text from "@/components/ui/text";
import { getEnumTKey } from "@/utils/strings";
import Tabs from "@/components/controls/Tabs";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import FileUpload from "@/components/controls/FileUpload/FileUpload";
import { SavedAttachmentInfo } from "@/entities/attachments/types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { EntityData } from "@/services/api/types";
import { useActivityModal } from "@/components/layouts/Activities/hooks/useActivityModal";
import { mergeActivitySubmissionNotes } from "@/components/layouts/Activities/hooks/useActivityModalSubmission";
import { forEach, isUndefined } from "lodash";
import CreatePersonModal from "@/components/layouts/CreatePersonModal";
import { getMustSelectCompanyText } from "@/entities/activities/helpers";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  entityType: EntityType;
  entityData: EntityData | undefined;
  pageName: string;
  isEditMode: boolean;
  editActivityData?: ActivityData | null;
  resetPage?: () => void;
}

const NDAActivityModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  entityType,
  entityData,
  pageName,
  isEditMode,
  editActivityData,
  resetPage
}) => {
  const { t } = useTranslation();
  const {
    formRef,
    isFormValid,
    setIsFormValid,
    handleSetValue,
    isCompanySet,
    activityData,
    handleSubmit: baseHandleSubmit,
    getBaseFields,
    getParticipantsField,
    processFieldsForEditMode,
    isCreatePersonModalOpen,
    setIsCreatePersonModalOpen,
    onPersonCreation,
    effectiveCompanyId
  } = useActivityModal({
    activityType: NewActivity.NDA,
    isOpen,
    setIsOpen,
    entityType,
    entityData,
    pageName,
    isEditMode,
    editActivityData,
    resetPage
  });

  const [ndaType, setNdaType] = useState(Activity.NDA_SENT.toString());
  const [fileAttachments, setFileAttachments] = useState<SavedAttachmentInfo[]>(
    []
  );
  const [attachmentsChanged, setAttachmentsChanged] = useState(false);
  const initializedActivityRef = useRef<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const newActivityKey = getEnumTKey(
    "activities",
    "newActivities",
    NewActivity,
    NewActivity.NDA
  );

  const getNdaTypeOptions = useCallback(() => {
    const options: { [key: string]: string } = {};
    forEach(
      [Activity.NDA_SENT.toString(), Activity.NDA_SIGNED.toString()],
      (type) => {
        options[type] = t(
          getEnumTKey("activities", "ndaTypes", Activity, Number(type))
        );
      }
    );
    return options;
  }, [t]);

  const formSections = useMemo<FormSectionProps[]>(() => {
    const baseFields = getBaseFields();

    const ndaFieldNames = ["date", "time", "participants"];

    let fields = ndaFieldNames
      .map((name) => {
        if (name === "participants") {
          const participantLabel = t(
            `activities.activityModal.${ndaType === Activity.NDA_SENT.toString() ? "sentTo" : "receivedFrom"}`
          );
          return getParticipantsField(participantLabel);
        }
        return baseFields[name as keyof typeof baseFields];
      })
      .filter(Boolean);

    fields = processFieldsForEditMode(fields);

    const timeFieldNames = ["date", "time"];
    const timeFields = fields.filter((field) =>
      timeFieldNames.includes(field.name)
    );
    const otherFields = fields.filter(
      (field) => !timeFieldNames.includes(field.name)
    );

    return [
      {
        id: "nda-time-section",
        fields: timeFields
      },
      {
        id: "nda-type-section",
        fields: [],
        bottomCustomComponent: (
          <div className="flex flex-row items-start gap-4 w-full">
            <Text variant="bodySecondary" className="w-[200px] shrink-0 pt-2">
              {t("activities.activityModal.ndaType")}
            </Text>
            <div className="flex-1">
              <Tabs
                dataTest="nda-type-tabs"
                options={getNdaTypeOptions()}
                defaultValue={ndaType}
                valueSelected={(value) => setNdaType(value)}
                className="w-full border-border-border2"
                size="lg"
              />
            </div>
          </div>
        )
      },
      {
        id: "nda-details-section",
        fields: otherFields
      }
    ];
  }, [
    ndaType,
    getBaseFields,
    getParticipantsField,
    processFieldsForEditMode,
    t,
    getNdaTypeOptions
  ]);

  useEffect(() => {
    if (!isOpen) {
      initializedActivityRef.current = null;
      return;
    }
    const activityKey = isEditMode ? activityData?.id : "new-nda";
    if (!activityKey || initializedActivityRef.current === activityKey) return;

    if (
      isEditMode &&
      !isUndefined(activityData) &&
      !isUndefined(activityData.type)
    ) {
      setNdaType(activityData.type.toString());

      // Set content for special fields
      setFileAttachments(activityData.notes?.attachments ?? []);
      setAttachmentsChanged(false);
      initializedActivityRef.current = activityKey;
      return;
    }
    if (!isEditMode) {
      initializedActivityRef.current = activityKey;
    }
  }, [activityData, isEditMode, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setFileAttachments([]);
      setAttachmentsChanged(false);
      setIsUploading(false);
      setNdaType(Activity.NDA_SENT.toString());
    }
  }, [isOpen]);

  const handleFileAttachments = (attachments: SavedAttachmentInfo[]) => {
    setFileAttachments(attachments);
    setAttachmentsChanged(true);
  };

  const handleUploadingChange = (uploading: boolean) => {
    setIsUploading(uploading);
  };

  const handleSubmit = async (data: ActivityDataInput) => {
    await baseHandleSubmit(data, (baseData) => ({
      ...mergeActivitySubmissionNotes(baseData, {
        ...(!isEditMode ? { meetingContent: "" } : {}),
        attachments: attachmentsChanged ? fileAttachments : undefined
      }),
      type: Number(ndaType)
    }));
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleCreateNew = () => {
    formRef.current?.requestSubmit();
  };

  return (
    <>
      <Modal
        open={isOpen}
        setIsOpen={setIsOpen}
        contentClassname="w-[35rem]"
        title={
          <Text as="div" variant="lgSubtitleH1">
            {t(`activities.activityModal.${isEditMode ? "edit" : "add"}`, {
              type: t(newActivityKey),
              activity: t("activities.activityModal.activity")
            })}
          </Text>
        }
        content={
          <div
            className={`flex flex-col gap-2 w-[35rem] p-6 h-fit overflow-y-auto max-h-[75vh]`}
          >
            {/* Hidden element to capture initial focus */}
            <div tabIndex={0} className="sr-only" />
            <FormNew
              sections={formSections}
              isFormValid={(isValid) => setIsFormValid(isValid)}
              onSubmit={handleSubmit}
              formRef={formRef}
              onSetValue={handleSetValue}
            />
            <div className="flex flex-col gap-2">
              <Text variant="bodySecondary">{`${t(newActivityKey)} files`}</Text>
              <FileUpload
                key={activityData?.id ?? "new-nda"}
                onAttachmentsChange={handleFileAttachments}
                onUploadingChange={handleUploadingChange}
                initialAttachments={
                  isEditMode ? activityData?.notes?.attachments : []
                }
              />
            </div>
          </div>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button
              data-test="cancel-button"
              variant="secondary"
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>
            <Tooltip>
              <TooltipTrigger>
                <Button
                  data-test="submit-button"
                  disabled={!isFormValid || isUploading || !isCompanySet}
                  onClick={handleCreateNew}
                >
                  {t(isEditMode ? "save" : "activities.activityModal.add", {
                    type: `${t(
                      getEnumTKey(
                        "activities",
                        "ndaTypes",
                        Activity,
                        Number(ndaType)
                      )
                    )} `,
                    activity: t("activities.activityModal.activity")
                  })}
                </Button>
              </TooltipTrigger>
              {!isCompanySet && (
                <TooltipContent maxWidth={15}>
                  {getMustSelectCompanyText(t, entityType)}
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        }
      />
      <CreatePersonModal
        isOpen={isCreatePersonModalOpen}
        setIsOpen={setIsCreatePersonModalOpen}
        onSuccess={onPersonCreation}
        companyId={effectiveCompanyId}
      />
    </>
  );
};

export default NDAActivityModal;
